import { Column } from 'components/common/Table/types/Column';

/**
 * Factory function to generate a type-safe array of column definitions for a generic table.
 *
 * @template T - The shape of the data object that each row in the table represents.
 *
 * @returns A function that takes a configuration object mapping keys of T to partial column definitions
 *          (excluding the `accessor`, which is automatically inferred from the key), and returns an array of complete Column<T> objects.
 *
 * @example
 * const columns = createColumns<UserData>()({
 *   firstName: { header: 'First Name', element: DefaultCell },
 *   email: { header: 'Email', element: EmailCell },
 * });
 */
export function createColumns<T>() {
  return <C extends Partial<Record<keyof T, Omit<Column<T>, 'accessor'>>>>(
    config: C
  ): Column<T>[] => {
    return Object.entries(config).map(([accessor, col]) => ({
      accessor: accessor as keyof T,
      ...(col as Omit<Column<T>, 'accessor'>),
    }));
  };
}
